import React from 'react';
import { Box } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { CopyBtn, MermaidBlock } from '../MermaidBlock';

const codeBlockChrome = {
  border: '1px solid #27272a',
  headerBg: '#1f1f23',
  headerBorder: '#2d2d32',
  bodyBg: '#111318',
};

const cleanedOneDark = {
  ...oneDark,
  'pre[class*="language-"]': {
    ...oneDark['pre[class*="language-"]'],
    background: codeBlockChrome.bodyBg,
    textShadow: 'none',
    fontFamily: '"SF Mono", SFMono-Regular, Consolas, "Liberation Mono", Menlo, monospace',
  },
  'code[class*="language-"]': {
    ...oneDark['code[class*="language-"]'],
    background: 'transparent',
    textShadow: 'none',
    fontFamily: '"SF Mono", SFMono-Regular, Consolas, "Liberation Mono", Menlo, monospace',
  },
};

function CodeBlock({ inline, className, children, node: _node, ...props }) {
  const codeString = String(children).replace(/\n$/, '');
  const match = /language-(\w+)/.exec(className || '');
  const language = match ? match[1] : '';

  if (inline) {
    return (
      <code className={className} {...props}>
        {children}
      </code>
    );
  }

  if (language === 'mermaid') {
    return <MermaidBlock chart={codeString} />;
  }

  return (
    <Box
      sx={{
        position: 'relative',
        my: 1.5,
        borderRadius: '10px',
        overflow: 'hidden',
        border: codeBlockChrome.border,
        '&:hover .code-copy-btn': { opacity: 1 },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 1.25,
          height: 36,
          backgroundColor: codeBlockChrome.headerBg,
          borderBottom: `1px solid ${codeBlockChrome.headerBorder}`,
        }}
      >
        <Box
          component="span"
          sx={{
            fontSize: '11px',
            fontWeight: 600,
            color: 'rgba(255,255,255,0.45)',
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            mr: 'auto',
          }}
        >
          {language || 'code'}
        </Box>
        <Box className="code-copy-btn" sx={{ opacity: 0, transition: 'opacity 150ms ease' }}>
          <CopyBtn text={codeString} />
        </Box>
      </Box>

      <SyntaxHighlighter
        style={cleanedOneDark}
        language={language || 'text'}
        PreTag="div"
        wrapLongLines
        customStyle={{
          margin: 0,
          borderRadius: 0,
          fontSize: '12.5px',
          lineHeight: 1.65,
          padding: '14px 16px',
          background: codeBlockChrome.bodyBg,
          overflowX: 'auto',
        }}
        {...props}
      >
        {codeString}
      </SyntaxHighlighter>
    </Box>
  );
}

const markdownSx = {
  '& p': { m: 0 },
  '& p + p': { mt: 1 },
  '& strong': { fontWeight: 700, color: 'var(--color-text-primary)' },
  '& em': { fontStyle: 'italic' },
  '& a': {
    color: '#4f46e5',
    textDecoration: 'none',
    borderBottom: '1px solid transparent',
    transition: 'border-color 150ms ease',
    '&:hover': { borderBottom: '1px solid #4f46e5' },
  },
  '& ul, & ol': { m: 0, mt: 0.5, mb: 0.5, pl: 2.5, '& li': { mb: 0.25 } },
  '& ul': { listStyleType: 'disc' },
  '& ol': { listStyleType: 'decimal' },
  '& code': {
    fontFamily: '"SF Mono", SFMono-Regular, Consolas, "Liberation Mono", Menlo, monospace',
    fontSize: '0.85em',
    backgroundColor: 'var(--color-action-hover)',
    borderRadius: '5px',
    px: 0.7,
    py: 0.2,
    color: '#d6336c',
    fontWeight: 500,
  },
  '& pre': { m: 0, '& code': { backgroundColor: 'transparent', border: 'none', p: 0, color: 'inherit', fontWeight: 400 } },
  '& blockquote': {
    m: 0,
    mt: 1,
    mb: 1,
    ml: 0,
    pl: 1.5,
    borderLeft: '2px solid var(--color-divider)',
    color: 'var(--color-text-secondary)',
    fontStyle: 'italic',
  },
  '& hr': { border: 'none', borderTop: '1px solid var(--color-divider)', my: 2 },
  '& table': {
    borderCollapse: 'collapse',
    width: '100%',
    my: 1.5,
    borderRadius: '8px',
    overflow: 'hidden',
    border: '1px solid var(--color-divider)',
  },
  '& th, & td': {
    border: '1px solid var(--color-divider)',
    px: 1.25,
    py: 0.75,
    fontSize: 'inherit',
    textAlign: 'left',
  },
  '& th': { backgroundColor: 'var(--color-bg-subtle)', fontWeight: 600, color: 'var(--color-text-primary)', fontSize: '0.9em' },
  '& h1, & h2, & h3, & h4, & h5, & h6': { mt: 2, mb: 0.75, lineHeight: 1.3, fontWeight: 700, color: 'var(--color-text-primary)' },
  '& h1': { fontSize: '1.3em' },
  '& h2': { fontSize: '1.2em' },
  '& h3': { fontSize: '1.1em' },
};

const markdownComponents = {
  code: CodeBlock,
};

const remarkPlugins = [remarkGfm];

function unwrapMarkdownFence(value) {
  if (typeof value !== 'string') return value;
  const match = value.match(/^\s*```(?:md|markdown)\s*\n([\s\S]*?)\n```\s*$/i);
  return match ? match[1] : value;
}

// Memoized so react-markdown only re-parses when the content string or sx
// reference actually changes. During streaming this keeps every settled
// message frozen while only the live message re-renders (callers must pass a
// stable `sx` reference for the skip to take effect).
const MarkdownContent = React.memo(function MarkdownContent({ children, sx }) {
  const normalizedContent = unwrapMarkdownFence(children);

  return (
    <Box className="markdown-content" sx={{ ...sx, ...markdownSx }}>
      <ReactMarkdown remarkPlugins={remarkPlugins} components={markdownComponents}>
        {normalizedContent}
      </ReactMarkdown>
    </Box>
  );
});

export default MarkdownContent;
